import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  MapPin, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  X,
  Image as ImageIcon,
  Video,
  Trash2,
  Send,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Info,
  Plus
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface IssueReportFormData {
  category: string;
  description: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  } | null;
  media: File[];
  isAnonymous: boolean;
}

const initialFormData: IssueReportFormData = {
  category: '',
  description: '',
  location: null,
  media: [],
  isAnonymous: false
};

const categories = [
  { value: 'waste_dump', label: 'Waste Dump', icon: '♻️' },
  { value: 'illegal_tree_cutting', label: 'Illegal Tree Cutting', icon: '🌳' },
  { value: 'water_contamination', label: 'Water Contamination', icon: '🌊' },
  { value: 'construction_pollution', label: 'Construction Pollution', icon: '🏗️' },
  { value: 'open_burning', label: 'Open Burning', icon: '🔥' },
  { value: 'other', label: 'Other', icon: '❓' }
];

interface IssueReportProps {
  onClose: () => void;
  onSuccess?: (points: number) => void;
}

export default function IssueScanner({ onClose, onSuccess }: IssueReportProps) {
  const [formData, setFormData] = useState<IssueReportFormData>(initialFormData);
  const [step, setStep] = useState<'capture' | 'details' | 'location' | 'review' | 'success' | 'error'>('capture');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaPreviews, setMediaPreviews] = useState<string[]>([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, profile } = useAuth();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    const newFiles = Array.from(files);
    
    // Validate file types and sizes
    const validFiles = newFiles.filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type.startsWith('video/');
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
      
      if (!isValidType) {
        setError('Only images and videos are allowed');
        return false;
      }
      
      if (!isValidSize) {
        setError('Files must be less than 10MB');
        return false;
      }
      
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    // Add new files to existing media
    setFormData(prev => ({
      ...prev,
      media: [...prev.media, ...validFiles]
    }));
    
    // Generate previews for the new files
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setMediaPreviews(prev => [...prev, e.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
    
    // Clear the input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
    
    setMediaPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleCaptureClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleGetLocation = () => {
    setIsGettingLocation(true);
    setLocationError(null);
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsGettingLocation(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // For demo purposes, we'll just use the coordinates as the address
        // In a real app, you would use a geocoding service to get the address
        const address = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        
        setFormData(prev => ({
          ...prev,
          location: {
            latitude,
            longitude,
            address
          }
        }));
        
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setLocationError(
          error.code === 1 
            ? 'Location permission denied. Please enable location services.'
            : 'Could not get your location. Please try again.'
        );
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to report an issue');
      setStep('error');
      return;
    }
    
    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    
    if (!formData.description || formData.description.trim().length < 10) {
      setError('Please provide a description (at least 10 characters)');
      return;
    }
    
    if (!formData.location) {
      setError('Please share your location');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      // Simulate submission for demo
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setSuccessMessage('Your issue has been reported successfully! You earned 50 EcoCoins.');
      
      // Call onSuccess callback with points earned
      if (onSuccess) {
        onSuccess(50);
      }
      
      setStep('success');
    } catch (error: any) {
      console.error('Error submitting issue report:', error);
      setError(`Error submitting issue report: ${error.message}`);
      setStep('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateAITags = (category: string, description: string): string[] => {
    // Simple tag generation based on category and keywords in description
    const baseTags = [category.replace('_', ' ')];
    
    const keywordMap: Record<string, string[]> = {
      'waste': ['trash', 'garbage', 'litter', 'plastic', 'dump'],
      'pollution': ['contamination', 'dirty', 'toxic', 'chemicals'],
      'water': ['river', 'lake', 'ocean', 'stream', 'flood'],
      'forest': ['tree', 'cutting', 'logging', 'deforestation'],
      'air': ['smoke', 'burning', 'emissions', 'smog']
    };
    
    // Add tags based on keywords in description
    const descLower = description.toLowerCase();
    for (const [tag, keywords] of Object.entries(keywordMap)) {
      if (keywords.some(keyword => descLower.includes(keyword))) {
        baseTags.push(tag);
      }
    }
    
    return [...new Set(baseTags)]; // Remove duplicates
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setMediaPreviews([]);
    setStep('capture');
    setError(null);
    setSuccessMessage(null);
    setUploadProgress(0);
  };

  const renderCaptureStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Camera className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Report Environmental Issue</h3>
        <p className="text-gray-600">
          Take photos or videos of environmental issues in your area to report them.
        </p>
      </div>

      {/* Media Capture */}
      <div className="space-y-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,video/*"
          multiple
          className="hidden"
        />
        
        {mediaPreviews.length === 0 ? (
          <div 
            onClick={handleCaptureClick}
            className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors cursor-pointer"
          >
            <div className="space-y-4">
              <div className="flex justify-center space-x-4">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <Camera className="h-8 w-8 text-emerald-600" />
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Video className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              
              <div>
                <p className="text-lg font-medium text-gray-900 mb-2">
                  Take photos or record videos
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Tap to capture evidence of the environmental issue
                </p>
                
                <button className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center space-x-2">
                  <Upload className="h-5 w-5" />
                  <span>Select Media</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative">
                  {formData.media[index]?.type.startsWith('video/') ? (
                    <video
                      src={preview}
                      className="w-full h-32 object-cover rounded-lg"
                      controls
                    />
                  ) : (
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  )}
                  <button
                    onClick={() => removeMedia(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              <div 
                onClick={handleCaptureClick}
                className="border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center h-32 cursor-pointer hover:border-emerald-400 transition-colors"
              >
                <div className="text-center">
                  <Plus className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                  <span className="text-sm text-gray-500">Add More</span>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setStep('details')}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors"
            >
              Continue
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderDetailsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Issue Details</h3>
        <p className="text-gray-600">
          Provide information about the environmental issue
        </p>
      </div>

      <div className="space-y-4">
        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
              className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <span className="flex items-center space-x-2">
                {formData.category ? (
                  <>
                    <span className="text-2xl">
                      {categories.find(c => c.value === formData.category)?.icon}
                    </span>
                    <span>
                      {categories.find(c => c.value === formData.category)?.label}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-500">Select a category</span>
                )}
              </span>
              {showCategoryDropdown ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </button>
            
            {showCategoryDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, category: category.value }));
                      setShowCategoryDropdown(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 text-left"
                  >
                    <span className="text-2xl">{category.icon}</span>
                    <span>{category.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe the environmental issue in detail..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            rows={4}
          />
        </div>

        {/* Anonymous Reporting */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="anonymous"
            checked={formData.isAnonymous}
            onChange={(e) => setFormData(prev => ({ ...prev, isAnonymous: e.target.checked }))}
            className="h-5 w-5 text-emerald-600 rounded focus:ring-emerald-500"
          />
          <div>
            <label htmlFor="anonymous" className="font-medium text-gray-900">
              Report Anonymously
            </label>
            <p className="text-sm text-gray-500">
              Your identity will not be shared publicly
            </p>
          </div>
        </div>

        <div className="flex space-x-3 pt-4">
          <button
            onClick={() => setStep('capture')}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-lg font-medium transition-colors"
          >
            Back
          </button>
          <button
            onClick={() => {
              if (!formData.category) {
                setError('Please select a category');
                return;
              }
              if (!formData.description || formData.description.trim().length < 10) {
                setError('Please provide a description (at least 10 characters)');
                return;
              }
              setError(null);
              setStep('location');
            }}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors"
          >
            Continue
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderLocationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="h-8 w-8 text-blue-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Share Location</h3>
        <p className="text-gray-600">
          We need the location of the environmental issue
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <MapPin className="h-6 w-6 text-blue-600 mt-1" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 mb-2">Location Information</h4>
            <p className="text-sm text-blue-700 mb-4">
              Your location helps authorities address the issue and contributes to our environmental mapping.
            </p>
            
            {formData.location ? (
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-gray-900">Location Captured</span>
                </div>
                <p className="text-sm text-gray-600">{formData.location.address}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Coordinates: {formData.location.latitude.toFixed(6)}, {formData.location.longitude.toFixed(6)}
                </p>
              </div>
            ) : (
              <button
                onClick={handleGetLocation}
                disabled={isGettingLocation}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                {isGettingLocation ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Getting Location...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>Share Location</span>
                  </>
                )}
              </button>
            )}
            
            {locationError && (
              <div className="mt-3 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 inline mr-1" />
                {locationError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={() => setStep('details')}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-lg font-medium transition-colors"
        >
          Back
        </button>
        <button
          onClick={() => {
            if (!formData.location) {
              setLocationError('Please share your location to continue');
              return;
            }
            setStep('review');
          }}
          disabled={!formData.location}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Review Your Report</h3>
        <p className="text-gray-600">
          Please review your environmental issue report before submitting
        </p>
      </div>

      <div className="space-y-4">
        {/* Category */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">
              {categories.find(c => c.value === formData.category)?.icon}
            </div>
            <div>
              <p className="text-sm text-gray-500">Category</p>
              <p className="font-medium text-gray-900">
                {categories.find(c => c.value === formData.category)?.label}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-500 mb-1">Description</p>
          <p className="text-gray-900">{formData.description}</p>
        </div>

        {/* Location */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <MapPin className="h-5 w-5 text-gray-500 mt-1" />
            <div>
              <p className="text-sm text-gray-500 mb-1">Location</p>
              <p className="text-gray-900">{formData.location?.address}</p>
            </div>
          </div>
        </div>

        {/* Media */}
        {mediaPreviews.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-500 mb-2">Media ({mediaPreviews.length})</p>
            <div className="grid grid-cols-3 gap-2">
              {mediaPreviews.map((preview, index) => (
                <div key={index} className="relative">
                  {formData.media[index]?.type.startsWith('video/') ? (
                    <div className="relative h-20 bg-black rounded">
                      <Video className="absolute inset-0 m-auto h-8 w-8 text-white" />
                    </div>
                  ) : (
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-20 object-cover rounded"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Anonymous */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            {formData.isAnonymous ? (
              <EyeOff className="h-5 w-5 text-gray-500" />
            ) : (
              <Eye className="h-5 w-5 text-gray-500" />
            )}
            <div>
              <p className="text-sm text-gray-500 mb-1">Reporting as</p>
              <p className="text-gray-900">
                {formData.isAnonymous ? 'Anonymous' : profile?.username || user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Submitting Report...</span>
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              <span>Submit Report</span>
            </>
          )}
        </button>

        {/* Back Button */}
        <button
          onClick={() => setStep('location')}
          disabled={isSubmitting}
          className="w-full bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 py-3 rounded-lg font-medium transition-colors"
        >
          Back
        </button>
      </div>

      {/* Upload Progress */}
      {isSubmitting && uploadProgress > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Uploading...</span>
            <span className="text-gray-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="h-10 w-10 text-green-600" />
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Report Submitted! 🎉</h3>
        <p className="text-gray-600 mb-4">
          {successMessage || 'Thank you for helping protect our environment'}
        </p>
        
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center justify-center space-x-2 text-emerald-700 mb-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="font-bold text-xl">+50 EcoCoins Earned!</span>
          </div>
          <p className="text-emerald-600 text-sm">
            Your report will be reviewed by our team and sent to the appropriate authorities.
          </p>
        </div>
      </div>

      <button
        onClick={resetForm}
        className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
      >
        Report Another Issue
      </button>
    </div>
  );

  const renderErrorStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
        <AlertCircle className="h-10 w-10 text-red-600" />
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h3>
        <p className="text-gray-600 mb-4">
          We couldn't submit your report at this time
        </p>
        
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-red-700">
            {error || 'An unexpected error occurred. Please try again.'}
          </p>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => setStep('review')}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 rounded-lg font-medium transition-colors"
        >
          Back to Review
        </button>
        <button
          onClick={resetForm}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors"
        >
          Start Over
        </button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (step) {
      case 'capture':
        return renderCaptureStep();
      case 'details':
        return renderDetailsStep();
      case 'location':
        return renderLocationStep();
      case 'review':
        return renderReviewStep();
      case 'success':
        return renderSuccessStep();
      case 'error':
        return renderErrorStep();
      default:
        return renderCaptureStep();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-6 border border-gray-200 max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <div className="absolute top-4 right-4">
          <button
            onClick={onClose}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {renderCurrentStep()}
      </div>
    </div>
  );
}